import React from 'react';
import { Container, Typography, Paper, Grid, Box } from '@mui/material';
import { PageHeader } from '../layout/PageHeader';
import { CallToAction } from '../common/CallToAction';
import { SEOHead, SEO_PAGES } from '../seo/SEOHead';
import SearchIcon from '@mui/icons-material/Search';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import TimelineIcon from '@mui/icons-material/Timeline';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import EmailIcon from '@mui/icons-material/Email';
import GroupsIcon from '@mui/icons-material/Groups';

export const ServicesPage: React.FC = () => {
  const services = [
    {
      icon: <SearchIcon fontSize="large" color="primary" />,
      title: "Intelligent Solicitation Analysis",
      description: "Our AI-powered system analyzes SAM.gov solicitations in real-time, extracting key information and requirements to help you make informed bidding decisions."
    },
    {
      icon: <AnalyticsIcon fontSize="large" color="primary" />,
      title: "Opportunity Assessment",
      description: "Get detailed insights into contract requirements, evaluation criteria, and competitive landscape to assess each opportunity's potential fit for your business."
    },
    {
      icon: <NotificationsActiveIcon fontSize="large" color="primary" />,
      title: "Smart Notifications",
      description: "Stay informed about new opportunities, deadline reminders, and updates to existing solicitations that match your business profile."
    },
    {
      icon: <TimelineIcon fontSize="large" color="primary" />,
      title: "Market Intelligence",
      description: "Access historical contract data and trends to understand pricing patterns, competition, and agency buying behaviors."
    },
    {
      icon: <AutoGraphIcon fontSize="large" color="primary" />,
      title: "Performance Analytics",
      description: "Track your bidding success rate, analyze win/loss patterns, and identify areas for improvement in your government contracting strategy."
    },
    {
      icon: <IntegrationInstructionsIcon fontSize="large" color="primary" />,
      title: "API Integration",
      description: "Seamlessly integrate our analysis capabilities into your existing workflows and systems through our robust API."
    },
    {
      icon: <EmailIcon fontSize="large" color="primary" />,
      title: "AI-Generated Quote Requests",
      description: "Generate customized, professional quote request emails tailored to each solicitation, sent directly through your Gmail account for a personalized touch."
    },
    {
      icon: <GroupsIcon fontSize="large" color="primary" />,
      title: "Smart Subcontractor Discovery",
      description: "Automatically identify and match potential subcontractors based on solicitation requirements, ensuring the perfect team for each contract opportunity."
    }
  ];

  return (
    <>
      <SEOHead {...SEO_PAGES.services} />
      <PageHeader
        title="Our Services"
        description="Comprehensive solutions for government contracting success"
      />
      <Container maxWidth="lg">
        <Box sx={{ mb: 6 }}>
          <Paper elevation={2} sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              How We Help You Succeed
            </Typography>
            <Typography paragraph>
              GovToolsPro provides a comprehensive suite of services designed to streamline your government 
              contracting process. From initial opportunity discovery to detailed analysis and strategic planning, 
              our tools and services help you make better decisions and improve your chances of winning contracts.
            </Typography>
          </Paper>
        </Box>

        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper elevation={2} sx={{ p: 4, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {service.icon}
                  <Typography variant="h6" component="h3" sx={{ ml: 2 }}>
                    {service.title}
                  </Typography>
                </Box>
                <Typography>
                  {service.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 6 }}>
          <Paper elevation={2} sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Custom Solutions
            </Typography>
            <Typography paragraph>
              Need a specialized solution for your organization? We offer customized services tailored to your 
              specific needs, including custom API integrations, specialized analytics, and dedicated support. 
              Contact us to learn more about how we can help your business succeed in government contracting.
            </Typography>
          </Paper>
        </Box>

        <CallToAction
          title="Ready to Enhance Your Government Contracting?"
          description="Access our full suite of AI-powered tools and services to streamline your contracting process and increase your win rate."
          buttonText="Explore Plans & Pricing"
          buttonLink="/plans"
          variant="contained"
        />
      </Container>
    </>
  );
};
